import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Users, TrendingUp, FileText, CheckCircle, Clock, AlertTriangle, DollarSign, Target, Award, Briefcase, Shield, RefreshCw } from 'lucide-react';
import OscopLogo from './brand/OscopLogo';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, ComposedChart } from 'recharts';

export default function CooperStatsView({ stats, activeChart, setActiveChart, fetchStats }) {
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0,00 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const { statistiques, cooper, demandes_acquises, evolution_mensuelle } = stats;
  const tauxReussite = statistiques?.taux_reussite || 0;

  // Custom tooltip pour les graphiques
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border">
          <p className="font-medium text-gray-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.name === 'CA' ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header avec profil COOPER — Premium navy/or O'SCOP */}
      <div className="group/header relative overflow-hidden rounded-2xl shadow-[0_20px_60px_-15px_rgba(10,61,98,0.45)]" data-testid="cooper-stats-header-premium">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a3d62] via-[#0c4a78] to-[#08304d]" />
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cg fill='%23DEA900' fill-opacity='0.5'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E\")" }} />
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#DEA900]/15 blur-3xl" />
        <div className="absolute -left-32 -bottom-24 w-80 h-80 rounded-full bg-[#DEA900]/10 blur-3xl" />

        <div className="relative p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Avatar initiales avec ring or institutionnel */}
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#DEA900] to-[#b88600] flex items-center justify-center text-[#0a3d62] text-2xl font-bold shadow-lg shadow-[#DEA900]/30 ring-1 ring-[#f5cc3e]/40">
                {(cooper?.prenom?.[0] || '') + (cooper?.nom?.[0] || '') || <OscopLogo size={36} variant="on-navy" />}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-[#f5cc3e] uppercase">O'SCOP Outremer</span>
                  <span className="h-px w-8 bg-[#f5cc3e]/40" />
                  <span className="text-[10px] tracking-wider text-white/60 uppercase">Tableau de bord COOPER'S</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight mt-0.5">{cooper?.prenom} {cooper?.nom}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-white/70">{cooper?.entreprise || "COOPER'S"}</p>
                  {cooper?.territoire && (
                    <Badge className="bg-[#DEA900]/22 hover:bg-[#DEA900]/30 text-[#f5cc3e] border border-[#DEA900]/40 font-semibold text-[10px]">
                      {cooper.territoire}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-right bg-white/8 backdrop-blur-md border border-white/15 rounded-xl px-4 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-[#f5cc3e]/80">Taux de réussite</p>
                <p className="text-4xl font-bold text-white tabular-nums leading-none mt-0.5">{tauxReussite}%</p>
              </div>
              <button
                onClick={fetchStats}
                className="group/btn flex items-center gap-1.5 text-[11px] text-white/60 hover:text-[#f5cc3e] transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5 transition-transform group-hover/btn:rotate-180" />
                Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques principales en grille */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Membres acquis */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100 rounded-bl-full opacity-50"></div>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Membres</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">
                  {statistiques?.total_membres_acquis || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">adhérents acquis</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dossiers traités */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-purple-100 rounded-bl-full opacity-50"></div>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Dossiers</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">
                  {statistiques?.total_dossiers || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">traités au total</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Taux de réussite */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-green-100 rounded-bl-full opacity-50"></div>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Réussite</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {tauxReussite}%
                </p>
                <p className="text-xs text-gray-400 mt-1">dossiers validés</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <Target className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <Progress value={tauxReussite} className="mt-3 h-2" />
          </CardContent>
        </Card>

        {/* Chiffre d'affaires */}
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-100 rounded-bl-full opacity-50"></div>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">CA Total</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {formatCurrency(statistiques?.chiffre_affaires || 0)}
                </p>
                <p className="text-xs text-gray-400 mt-1">généré</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Graphiques d'évolution */}
      {evolution_mensuelle && evolution_mensuelle.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Évolution sur 12 mois
                </CardTitle>
                <CardDescription>
                  Progression de votre activité mois par mois
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveChart('ca')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeChart === 'ca' 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  💰 CA
                </button>
                <button
                  onClick={() => setActiveChart('dossiers')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeChart === 'dossiers' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📁 Dossiers
                </button>
                <button
                  onClick={() => setActiveChart('membres')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeChart === 'membres' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  👥 Membres
                </button>
                <button
                  onClick={() => setActiveChart('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeChart === 'all' 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📊 Tout
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                {activeChart === 'ca' ? (
                  <AreaChart data={evolution_mensuelle}>
                    <defs>
                      <linearGradient id="colorCA" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="mois" 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickFormatter={(value) => `${value}€`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="ca" 
                      name="CA"
                      stroke="#10b981" 
                      strokeWidth={3}
                      fill="url(#colorCA)" 
                    />
                  </AreaChart>
                ) : activeChart === 'dossiers' ? (
                  <BarChart data={evolution_mensuelle}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="mois" 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="dossiers" 
                      name="Dossiers"
                      fill="#9333ea" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                ) : activeChart === 'membres' ? (
                  <LineChart data={evolution_mensuelle}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="mois" 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line 
                      type="monotone" 
                      dataKey="membres" 
                      name="Membres"
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                ) : (
                  <ComposedChart data={evolution_mensuelle}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="mois" 
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12 }} 
                      tickLine={false}
                      axisLine={{ stroke: '#e5e7eb' }}
                      tickFormatter={(value) => `${value}€`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar 
                      yAxisId="left"
                      dataKey="dossiers" 
                      name="Dossiers"
                      fill="#9333ea" 
                      radius={[4, 4, 0, 0]}
                    />
                    <Line 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="membres" 
                      name="Membres"
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6' }}
                    />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="ca" 
                      name="CA"
                      stroke="#10b981" 
                      strokeWidth={2}
                      dot={{ fill: '#10b981' }}
                    />
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Détails des dossiers et dommages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* État des dossiers */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              État des dossiers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium">Devis en cours</span>
              </div>
              <Badge className="bg-yellow-600 text-white border-yellow-800 font-semibold">
                {statistiques?.devis_en_cours || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">Dossiers clos</span>
              </div>
              <Badge className="bg-green-700 text-white border-green-900 font-semibold">
                {statistiques?.dossiers_clos || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium">Demandes achetées</span>
              </div>
              <Badge className="bg-blue-700 text-white border-blue-900 font-semibold">
                {statistiques?.demandes_achetees || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Suivi des dommages */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Suivi des dommages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium">Total déclarés</span>
              </div>
              <Badge className="bg-red-700 text-white border-red-900 font-semibold">
                {statistiques?.dommages?.total || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium">En cours de traitement</span>
              </div>
              <Badge className="bg-orange-600 text-white border-orange-800 font-semibold">
                {statistiques?.dommages?.en_cours || 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">Clôturés</span>
              </div>
              <Badge className="bg-green-700 text-white border-green-900 font-semibold">
                {statistiques?.dommages?.clos || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Adhésions FTPE */}
      {statistiques?.ftpe && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Adhésions FTPE de vos membres
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{statistiques.ftpe.actives || 0}</p>
                <p className="text-sm text-gray-600">Actives</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-3xl font-bold text-yellow-600">{statistiques.ftpe.en_attente || 0}</p>
                <p className="text-sm text-gray-600">En attente</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{statistiques.ftpe.expirees || 0}</p>
                <p className="text-sm text-gray-600">Expirées</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique des membres acquis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Vos membres adhérents
          </CardTitle>
          <CardDescription>
            Liste de vos membres avec leurs souscriptions et CA généré
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!demandes_acquises || demandes_acquises.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun membre adhérent acquis pour le moment</p>
              <p className="text-sm text-gray-400 mt-1">
                Commencez à accompagner des membres pour voir vos statistiques ici
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {demandes_acquises.slice(0, 10).map((demande, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600">
                      {(demande.membre?.prenom?.[0] || '') + (demande.membre?.nom?.[0] || '')}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {demande.membre?.prenom} {demande.membre?.nom}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {demande.membre?.entreprise || demande.membre?.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-right">
                    <div>
                      <p className="text-sm text-gray-500">Souscriptions</p>
                      <p className="font-semibold text-blue-600">
                        {demande.souscriptions_validees || 0} / {demande.nb_souscriptions || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">CA généré</p>
                      <p className="font-semibold text-green-600">
                        {formatCurrency(demande.ca_genere || 0)}
                      </p>
                    </div>
                    <div className="text-xs text-gray-400">
                      {formatDate(demande.date_acquisition)}
                    </div>
                  </div>
                </div>
              ))}
              {demandes_acquises.length > 10 && (
                <p className="text-center text-sm text-gray-500 pt-2">
                  + {demandes_acquises.length - 10} autres membres
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
